import type { Point3 } from "@common/utils/operatorRig";

function tint(color: number, target: number, amount: number): number {
    let result = 0;
    for (const shift of [16, 8, 0]) {
        const channel = (color >> shift & 255) * (1 - amount) + (target >> shift & 255) * amount;
        result |= Math.round(channel) << shift;
    }
    return result;
}

/** Paint existing polygons once per material/topology change. No extra geometry,
 * textures, per-pixel effects or changes to the shared damage silhouette. */
export function operatorFacePaint(name: string, vertices: Point3[], faces: number[][], color: number, accent: number): Uint32Array {
    const paint = new Uint32Array(faces.length);
    paint.fill(color);
    const shade = tint(color, 0x172b36, 0.40), panel = tint(color, 0xe3ebcf, 0.22);
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const v of vertices) {
        minX = Math.min(minX, v.x); maxX = Math.max(maxX, v.x);
        minY = Math.min(minY, v.y); maxY = Math.max(maxY, v.y);
        minZ = Math.min(minZ, v.z); maxZ = Math.max(maxZ, v.z);
    }
    for (let f = 0; f < faces.length; f++) {
        let x = 0, y = 0, z = 0;
        const face = faces[f];
        for (const i of face) { x += vertices[i].x; y += vertices[i].y; z += vertices[i].z; }
        x = (x / face.length - minX) / (maxX - minX || 1) * 2 - 1;
        y = (y / face.length - minY) / (maxY - minY || 1) * 2 - 1;
        z = (z / face.length - minZ) / (maxZ - minZ || 1) * 2 - 1;
        if (/^(premium|sovereignRoyal)/.test(name)) {
            paint[f] = z > .35 ? tint(color,0xfff3cb,.23) : z < -.35 ? tint(color,0x12152b,.36)
                : Math.abs(y) > .65 ? tint(color,accent,.20) : color;
        } else if (name === "helmetShell" || name === "helmetEquipmentShell") {
            // Broad crown and side panels avoid thin, jagged stripes at game scale.
            paint[f] = Math.abs(y) > 0.64 && z < 0.15 ? shade
                : z > 0.50 ? tint(panel, name === "helmetEquipmentShell" ? 0xb6d760 : accent, 0.08) : color;
        } else if (name === "visor") {
            const row = Math.floor(f / 8), column = f % 8;
            paint[f] = row === 2 ? tint(color, 0x103c59, 0.58)
                : column === 2 || column === 3 ? tint(color, 0xd6fff4, 0.27) : tint(color, 0x174c69, 0.20);
        } else if (name === "visorGlint") {
            paint[f] = f % 8 < 3 ? 0xe0fff5 : 0x91ddd8;
        } else if (name === "torso") {
            paint[f] = z < -0.55 || Math.abs(y) > 0.67 ? shade : x > 0.40 && z > -0.1 ? panel : color;
        } else if (/^(shoulder|upperArm|sleeveRoll)/.test(name)) {
            paint[f] = z > 0.45 ? panel : z < -0.35 ? shade : color;
        } else if (/^(thigh|shin|trouserFold)/.test(name)) {
            paint[f] = Math.abs(y) > 0.65 ? shade : x > 0.45 ? panel : color;
        } else if (/^(bootToe|bootAnkle|knee)/.test(name) && !name.startsWith("kneeRivet")) {
            paint[f] = z > 0.25 ? panel : shade;
        } else if (/^boot-?1$/.test(name)) {
            paint[f] = x > 0.45 ? shade : color;
        } else if (/^glove-?1$/.test(name)) {
            paint[f] = z > 0.30 ? 0x95a28a : 0x48544e;
        } else if (/^(vestPlate|vestBack|vestHeavyChest|vestHeavyBack|vestPauldron)/.test(name)) {
            paint[f] = Math.abs(y) > 0.6 || z < -0.55 ? shade : z > 0.5 ? panel
                : x > 0.7 && Math.abs(y) < 0.35 ? tint(color, accent, 0.16) : color;
        } else if (name === "backpack" || name === "packFlap" || name.startsWith("packPocket")) {
            paint[f] = Math.abs(y) > 0.62 ? shade : z > 0.5 ? panel : color;
        }
    }
    return paint;
}

export function operatorPaintPreview(hostname: string, search: string): boolean {
    return ["127.0.0.1", "localhost", "[::1]"].includes(hostname)
        && new URLSearchParams(search).get("momoOperator") === "painted";
}
